import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  ACTIVITY_CLOSED,
  ACTIVITY_EXECUTION,
  ACTIVITY_PLANNING,
  ACTIVITY_PREPARATION,
  ACTIVITY_REPORTING,
  ACTIVITY_SURVEILLANCE_FINDING
} from "../config";
import {
  PERMISSION_EXPORT_QAM_AUDITS,
  PERMISSION_MANAGE_QAM_ALL_AUDITS,
  PERMISSION_MANAGE_QAM_AUDITS
} from "../../../components/_auth/permissions/permissions";
import TableCell from "../../../components/SmartTable/TableCell";
import Tooltip from "../../../components/Tooltip";
import { PAGE_QAM_ACTIVITY_ACTIONS, PAGE_QAM_ACTIVITY_CHECKLIST, PAGE_QAM_ACTIVITY_PROGRAM } from "../routes";
import { openModalAction } from "../../../components/Modal/reducer";
import DeleteItemButton from "../../../components/SmartTable/TableFiltersActions/DeleteItemButton";
import { usePermission } from "../../../components/_auth/permissions/usePermission";
import { useIsTablet } from "../../../utils/hooks/useIsTablet";
import Popover from "../../../components/Popover";
import { downloadActivityReportPdf, downloadActivityReportZip, TABLE_QAM_ACTIVITIES } from "./methods";
import { MODAL_QAM_ACTIVITY_FORM } from "./index";

const ActivityActionColumn = ({ row }) => {
  const dispatch = useDispatch();
  const permission = usePermission();
  const { t } = useTranslation();
  const history = useHistory();
  const isTablet = useIsTablet();
  const [actionsOpen, setActionsOpen] = useState(false);

  const programAvailable = row.stage_name === ACTIVITY_PLANNING || row.stage_name === ACTIVITY_PREPARATION;
  const checklistAvailable = row.stage_name === ACTIVITY_EXECUTION;
  const actionsAvailable =
    row.stage_name === ACTIVITY_REPORTING ||
    row.stage_name === ACTIVITY_SURVEILLANCE_FINDING ||
    row.stage_name === ACTIVITY_CLOSED;

  const auditCanBeExported = permission.canDo(PERMISSION_EXPORT_QAM_AUDITS);
  const activityIsEditableOrRemovable = permission.canDo(PERMISSION_MANAGE_QAM_ALL_AUDITS) || !!row.canManage;
  const activityCanBeRemoved =
    activityIsEditableOrRemovable &&
    (row.stage_name === ACTIVITY_PLANNING ||
      row.stage_name === ACTIVITY_PREPARATION ||
      row.stage_name === ACTIVITY_EXECUTION);
  const activityCanBeEdited =
    activityIsEditableOrRemovable &&
    (row.stage_name === ACTIVITY_PLANNING ||
      row.stage_name === ACTIVITY_PREPARATION ||
      row.stage_name === ACTIVITY_EXECUTION);
  const canManageActivities = permission.canDo([
    PERMISSION_MANAGE_QAM_AUDITS,
    PERMISSION_MANAGE_QAM_ALL_AUDITS,
    PERMISSION_EXPORT_QAM_AUDITS
  ]);

  let exportType = "Program";
  let exportTooltipTransKey = "qam.activities.export_program_pdf";
  if (row.stage_name === ACTIVITY_EXECUTION) {
    exportType = "Checklist";
    exportTooltipTransKey = "qam.activities.export_checklist_pdf";
  }
  if (
    row.stage_name === ACTIVITY_REPORTING ||
    row.stage_name === ACTIVITY_SURVEILLANCE_FINDING ||
    row.stage_name === ACTIVITY_CLOSED
  ) {
    exportType = "Report";
    exportTooltipTransKey = "qam.activities.export_report_pdf";
  }

  const actionButtons = (
    <>
      <Tooltip title={t("qam.activities.program_popover")} placement="top">
        <button
          type="button"
          disabled={!programAvailable}
          className="text-link mr-10"
          onClick={() => {
            if (programAvailable) {
              history.push(`${PAGE_QAM_ACTIVITY_PROGRAM}/${row.id}`);
            }
          }}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          <i className="ti-list" />
        </button>
      </Tooltip>
      <Tooltip title={t("qam.activities.checklist_popover")} placement="top">
        <button
          type="button"
          disabled={!checklistAvailable}
          className="text-link mr-10"
          onClick={() => {
            if (checklistAvailable) {
              history.push(`${PAGE_QAM_ACTIVITY_CHECKLIST}/${row.id}`);
            }
          }}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          <i className="ti-clipboard" />
        </button>
      </Tooltip>
      <Tooltip title={t("qam.activities.actions_popover")} placement="top">
        <button
          type="button"
          disabled={!actionsAvailable}
          className="text-link"
          onClick={() => {
            if (actionsAvailable) {
              history.push(`${PAGE_QAM_ACTIVITY_ACTIONS}/${row.id}`);
            }
          }}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          <i className="ti-layout-list-thumb" />
        </button>
      </Tooltip>
      {canManageActivities && (
        <>
          {auditCanBeExported && (
            <Tooltip title={t(exportTooltipTransKey)} placement="top" className="text-muted">
              <button
                type="button"
                onClick={() => downloadActivityReportPdf(t, dispatch, row, exportType)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
              >
                <i className="ti-download" />
              </button>
            </Tooltip>
          )}
          <Tooltip title={t("qam.activities.export_zip")} placement="top" className="text-muted">
            <button
              type="button"
              onClick={() => downloadActivityReportZip(t, dispatch, row, exportType)}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
            >
              <i className="ti-zip" />
            </button>
          </Tooltip>
          <Tooltip title={t("qam.activities.edit_activity")} placement="top" className="text-primary">
            <button
              type="button"
              disabled={!activityCanBeEdited}
              className="text-primary"
              onClick={() => {
                if (activityCanBeEdited) {
                  dispatch(openModalAction({ name: MODAL_QAM_ACTIVITY_FORM, data: row }));
                }
              }}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
            >
              <i className="ti-pencil-alt" />
            </button>
          </Tooltip>
          <DeleteItemButton
            disabled={!activityCanBeRemoved}
            tableName={TABLE_QAM_ACTIVITIES}
            route={`qam/audits/${row.id}`}
            content={
              <div>
                <div>{t("container.confirm_modal.form_subtitle")}</div>
              </div>
            }
          />
        </>
      )}
    </>
  );

  const getMinWidth = () => {
    if (isTablet) return 40;
    return canManageActivities ? 135 : 62;
  };

  return (
    <TableCell minWidth={getMinWidth()} className="table-actions-column">
      {isTablet ? (
        <Popover
          trigger="click"
          placement="bottomRight"
          open={actionsOpen}
          onOpenChange={setActionsOpen}
          content={
            <div className="activity-actions-popover" onClickCapture={() => setActionsOpen(false)}>
              {actionButtons}
            </div>
          }
        >
          <button
            type="button"
            onClick={(e) => e.preventDefault()}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
          >
            <i className="ti-more" />
          </button>
        </Popover>
      ) : (
        actionButtons
      )}
    </TableCell>
  );
};

export default ActivityActionColumn;
